import { useEffect, useState } from 'react'
import { costLagForAccounts } from '@qim/shared'
import { fetchDailyFleetCost, fetchInstanceCosts } from '../api.js'
import { formatMoney } from '../format.js'
import { BudgetDetails, useBudget } from './budget.jsx'
import { CostChart } from './cost-chart.jsx'
import type { DailyFleetCost, InstanceCostsWire } from '../api.js'
import type { JSX } from 'react'

/**
 * The fleet's Cost tab: what Qrvey's own accounts have spent this month, against
 * the platform budget (IM-130, IM-131, IM-152).
 *
 * ## Why it reads on open, when IM-130's panel waited for a click
 *
 * That panel spanned every customer, and every `GetCostAndUsage` it made billed
 * **the customer's** account — so it was asked for, never rendered. This tab is
 * Qrvey's own accounts only (the API reads nothing else), so a read charges
 * Qrvey, and the series is cached for twelve hours against the period. Opening
 * the tab *is* the ask: the tab that is not open — Instances, the front door —
 * makes no cost call at all, which is the IM-83 rule for tabs.
 *
 * ## Why the per-instance breakdown is still a button
 *
 * IM-131's read is uncached and one charged request per account, and it is the
 * question asked far less often. So the totals arrive with the tab, and the
 * split is asked for.
 *
 * ## No verdict, here either
 *
 * A dollar figure beside an instance makes the temptation stronger, and the
 * standing rule does not bend for it. This reports what is spent. Nothing is
 * called waste, nothing is ranked as a candidate for anything, and there is no
 * destroy link.
 */

type Breakdown =
  | {
      readonly state: 'answered'
      readonly accountKey: string
      readonly label: string
      readonly costs: InstanceCostsWire
    }
  | { readonly state: 'failed'; readonly accountKey: string; readonly label: string }

export const FleetCostTab = ({
  baseUrl,
  role,
}: {
  readonly baseUrl: string
  readonly role: string
}): JSX.Element => {
  const [cost, setCost] = useState<DailyFleetCost | undefined>(undefined)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const budget = useBudget(baseUrl, role)

  useEffect(() => {
    let live = true
    void fetchDailyFleetCost(baseUrl)
      .then((answer) => {
        if (live) setCost(answer)
      })
      .catch(() => {
        if (live) setProblem('Could not read this month’s costs just now.')
      })
    return () => {
      live = false
    }
  }, [baseUrl])

  const platformBudget = budget.status?.budget

  return (
    <>
      <section className="panel">
        <h2>Cost this month</h2>
        <p className="hint">
          Qrvey&rsquo;s own accounts only &mdash; customers&rsquo; accounts are on each
          customer&rsquo;s Cost tab. Read from each account&rsquo;s cloud billing when this tab
          opens and kept for twelve hours.
        </p>
        {problem === undefined ? null : (
          <p role="alert" className="failure">
            {problem}
          </p>
        )}
        {cost === undefined ? (
          problem === undefined ? (
            <p role="status">Reading…</p>
          ) : null
        ) : (
          <CostChart
            period={cost.period}
            series={cost.days}
            currency={cost.currency}
            budget={
              platformBudget === undefined
                ? undefined
                : {
                    amount: platformBudget.monthlyAmount,
                    warnAtPercent: platformBudget.warnAtPercent,
                  }
            }
            unreadable={cost.unreadable.length}
            lag={costLagForAccounts(
              [...cost.accounts, ...cost.unreadable].map((a) => a.accountKey),
            )}
          />
        )}
      </section>

      {cost === undefined ? null : <ByAccount cost={cost} />}
      {cost === undefined || cost.accounts.length === 0 ? null : (
        <ByInstance baseUrl={baseUrl} cost={cost} />
      )}

      <BudgetDetails
        baseUrl={baseUrl}
        role={role}
        status={budget.status}
        failure={budget.failure}
        onSaved={() => void budget.reload()}
      />
    </>
  )
}

const share = (amount: number, total: number): string =>
  total <= 0 ? '—' : `${String(Math.round((amount / total) * 100))}%`

const ByAccount = ({ cost }: { readonly cost: DailyFleetCost }): JSX.Element => (
  <section className="panel">
    <h3>
      By account{' '}
      <span className="muted">
        · {cost.accounts.length} read, {cost.period.describes ?? cost.period.from}
      </span>
    </h3>
    {cost.accounts.length === 0 ? (
      <p role="status">No account answered with a cost figure.</p>
    ) : (
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th scope="col">Account</th>
              <th scope="col">Customer</th>
              <th scope="col">Spend</th>
              <th scope="col">Share</th>
            </tr>
          </thead>
          <tbody>
            {cost.accounts.map((row) => (
              <tr key={`${row.tenantId}/${row.accountKey}`}>
                <td className="mono-cell">
                  {row.accountKey}
                  <p className="muted">{row.label}</p>
                </td>
                <td>{row.tenantName}</td>
                <td>{formatMoney(row.amount, cost.currency)}</td>
                <td>{share(row.amount, cost.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    {/* Named rather than dropped: a total that quietly omits accounts looks
      complete and is not. */}
    {cost.unreadable.length === 0 ? null : (
      <>
        <p className="hint">Not included, because their cost could not be read:</p>
        <ul className="unread-list">
          {cost.unreadable.map((gap) => (
            <li key={`${gap.tenantId}/${gap.accountKey}`}>
              <span className="mono-cell">{gap.accountKey}</span> {gap.label} ({gap.tenantName})
              <p className="muted">{gap.reason}</p>
            </li>
          ))}
        </ul>
      </>
    )}
  </section>
)

const ByInstance = ({
  baseUrl,
  cost,
}: {
  readonly baseUrl: string
  readonly cost: DailyFleetCost
}): JSX.Element => {
  const [rows, setRows] = useState<readonly Breakdown[] | undefined>(undefined)
  const [busy, setBusy] = useState(false)

  const read = async (): Promise<void> => {
    setBusy(true)
    const answers = await Promise.all(
      cost.accounts.map(async (account): Promise<Breakdown> => {
        try {
          const { costs } = await fetchInstanceCosts(baseUrl, account.tenantId, account.accountKey)
          return { state: 'answered', accountKey: account.accountKey, label: account.label, costs }
        } catch {
          return { state: 'failed', accountKey: account.accountKey, label: account.label }
        }
      }),
    )
    setRows(answers)
    setBusy(false)
  }

  const lines = (rows ?? []).flatMap((row) =>
    row.state === 'answered' && row.costs.state === 'read'
      ? [
          ...row.costs.instances.map((i) => ({
            key: `${row.accountKey}/${i.deploymentId}`,
            what: i.deploymentId,
            remainder: false,
            accountKey: row.accountKey,
            amount: i.amount,
            currency: row.costs.state === 'read' ? row.costs.currency : cost.currency,
          })),
          // The remainder is its own row, never hidden: in the month it was
          // measured it was 17% of the account.
          {
            key: `${row.accountKey}/(unattributed)`,
            what: 'Not attributed to an instance',
            remainder: true,
            accountKey: row.accountKey,
            amount: row.costs.unattributed,
            currency: row.costs.currency,
          },
        ]
      : [],
  )
  const unanswered = (rows ?? []).filter(
    (row) => row.state === 'failed' || row.costs.state !== 'read',
  )
  const truncated = (rows ?? []).filter(
    (row) => row.state === 'answered' && row.costs.state === 'read' && row.costs.truncated === true,
  )

  return (
    <section className="panel">
      <h3>By instance</h3>
      <p className="hint">
        By the installer&rsquo;s <code>qrvey-id</code> tag. One uncached read per account, so it
        happens when you ask.
      </p>
      <button type="button" className="ghost" disabled={busy} onClick={() => void read()}>
        {rows === undefined ? 'Break down by instance' : 'Read again'}
      </button>
      {busy ? <p role="status">Reading…</p> : null}
      {rows === undefined || lines.length === 0 ? null : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">Instance</th>
                <th scope="col">Account</th>
                <th scope="col">Spend</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.key}>
                  <td>{line.remainder ? <em>{line.what}</em> : line.what}</td>
                  <td className="mono-cell">{line.accountKey}</td>
                  <td>{formatMoney(line.amount, line.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {truncated.length === 0 ? null : (
        <p className="hint">
          The cloud had more instances than one page held for{' '}
          {truncated.map((row) => row.accountKey).join(', ')}, so those breakdowns are short.
        </p>
      )}
      {unanswered.length === 0 ? null : (
        <ul className="unread-list">
          {unanswered.map((row) => (
            <li key={row.accountKey}>
              <span className="mono-cell">{row.accountKey}</span> {row.label}
              <p className="muted">
                {row.state === 'failed'
                  ? 'Could not read the per-instance breakdown just now.'
                  : row.costs.state === 'read'
                    ? ''
                    : row.costs.reason}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
